import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Typography, Container, Box, Tabs, Tab, useMediaQuery } from '@mui/material';
import SideMenu from '../components/SideMenu';
import axios from 'axios';
import { useUser } from '../context/UserContext';

// Import the separated components
import Sites from '../settings/Sites';
import Fleet from '../settings/Fleet';
import Users from '../settings/Users';
import Drivers from '../settings/Drivers';
import RateManagement from './Settings/RateManagement'; // Import the new component
import BackgroundChecksSettings from '../settings/BackgroundChecksSettings'; // Import Background Checks component
import InsuranceSettings from '../settings/InsuranceSettings'; // Import Insurance component
import IncentiveProgram from '../settings/IncentiveProgram'; // On-time arrival incentive program (owners only)
// Website, marketing and hiring tools live in the Owner Console (src/pages/OwnerConsole), not here
import DashboardSettings from '../settings/DashboardSettings'; // Import Dashboard Settings component
import TermsSettings from '../settings/TermsSettings'; // Customer terms & signatures (owners/RM/SP/Org 2 admins)
import HiringMarketingLink from '../settings/HiringMarketingLink'; // Pointer to the Owner Console (OW/sOW only)
import { settingsAccess } from '../utils/settingsAccess';
import { usePageTour } from '../tours/usePageTour';

function Settings() {
  const isSmallScreen = useMediaQuery('(max-width:600px)');
  const [tabValue, setTabValue] = useState(0);
  const { currentUser } = useUser();
  usePageTour('settings', !!currentUser);

  // One matrix decides every tab (see src/utils/settingsAccess.js; the API enforces the same)
  const access = useMemo(() => settingsAccess(currentUser), [currentUser]);
  const orgAdminReadOnly = access.orgAdmin === 'read';
  const sitesRatesReadOnly = access.sitesRates === 'read';
  // Insurance and background-check lists are not vendor-scoped for providers yet, so Org 1 only
  const isOrg1AdminUp = access.dispatchSettings;

  // State for data fetched from backend
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [sites, setSites] = useState([]); // This will hold Facility data
  const [vehicles, setVehicles] = useState([]);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [usersPage, setUsersPage] = useState(0); // Pagination state for users
  const [usersRowsPerPage, setUsersRowsPerPage] = useState(10); // Pagination state for users
  const [usersTotalCount, setUsersTotalCount] = useState(0); // Total count for users pagination
  const [sitesPage, setSitesPage] = useState(0); // Pagination state for sites
  const [sitesRowsPerPage, setSitesRowsPerPage] = useState(10); // Pagination state for sites
  const [sitesTotalCount, setSitesTotalCount] = useState(0); // Total count for sites pagination
  const [fleetPage, setFleetPage] = useState(0); // Pagination state for fleet
  const [fleetRowsPerPage, setFleetRowsPerPage] = useState(10); // Pagination state for fleet
  const [fleetTotalCount, setFleetTotalCount] = useState(0); // Total count for fleet pagination
const [drivers, setDrivers] = useState([]); // State for driver data
  const [loadingDrivers, setLoadingDrivers] = useState(false); // Loading state for drivers
  const [driversPage, setDriversPage] = useState(0); // Pagination state for drivers
  const [driversRowsPerPage, setDriversRowsPerPage] = useState(10); // Pagination state for drivers
  const [driversTotalCount, setDriversTotalCount] = useState(0); // Total count for drivers pagination
  const [loadingVehicles, setLoadingVehicles] = useState(false);
  const [loadingFacilities, setLoadingFacilities] = useState(false); // Added loading state for facilities
  const [fetchError, setFetchError] = useState(null);

  // Helper function to get auth headers (memoized)
  const getAuthHeader = useCallback(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      // console.error("Authentication token not found."); // Removed log
      // Optionally redirect to login or handle error
      window.location.href = '/login';
      return null;
    }
    return { Authorization: `Bearer ${token}` };
  }, []);

  // Fetch initial data on mount
  useEffect(() => {
    const headers = getAuthHeader();
    if (!headers) return; // Don't fetch if no token

    fetchUsers({}, 0, 10, headers); // Pass initial pagination and headers
    fetchVehicles({}, 0, 10, headers); // Pass initial pagination (page 0, 10 rows) and headers
    fetchFacilities({}, headers, true, 0, 10); // Pass headers, updateState=true, initial page=0, rows=10
    fetchOtherSettings(headers); // Pass headers
fetchDrivers({}, 0, 10, headers); // Fetch initial drivers
  }, [getAuthHeader]); // Depend on memoized getAuthHeader

  // Tabs this user may see, in display order; the selected index is into this list
  const visibleTabs = useMemo(() => [
    { key: 'rates', label: 'Rate Management', show: access.sitesRates !== 'none' },
    { key: 'dashboard', label: 'Dashboard', show: true },
    { key: 'sites', label: 'Sites', show: access.sitesRates !== 'none' },
    { key: 'fleet', label: 'Fleet', show: access.orgAdmin !== 'none' },
    { key: 'users', label: 'Users', show: access.orgAdmin !== 'none' },
    { key: 'drivers', label: 'Drivers', show: access.orgAdmin !== 'none' },
    { key: 'backgroundChecks', label: 'Background Checks', show: isOrg1AdminUp },
    { key: 'insurance', label: 'Insurance', show: isOrg1AdminUp },
    { key: 'incentives', label: 'Incentives', show: ['OW', 'sOW', 'OWp'].includes(currentUser?.primaryRole) },
    { key: 'hiringMarketing', label: 'Hiring & Marketing Management', show: access.ownerConsole },
    { key: 'terms', label: 'Terms & Signatures', show: access.orgAdmin !== 'none' }
  ].filter((t) => t.show), [access, isOrg1AdminUp, currentUser]);

  const activeTab = visibleTabs[tabValue]?.key;

  // A role change (or a deep link) can leave the index past the tabs this user has
  useEffect(() => {
    if (visibleTabs.length && tabValue >= visibleTabs.length) setTabValue(0);
  }, [visibleTabs, tabValue]);

  // Fetch other general settings (e.g., locations)
  const fetchOtherSettings = async (headers) => {
     try {
       const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/settings`, { headers }); // Use passed headers
       const { locations, selectedLocation /* removed sites */ } = response.data;
       setLocations(locations || []);
       setSelectedLocation(selectedLocation || '');
       // setSites(sites || []); // Remove sites from here
     } catch (error) {
       // console.error('Error fetching other settings:', error); // Removed log
       setFetchError(prev => ({ ...prev, other: 'Failed to load location/rate settings.' }));
       if (error.response && error.response.status === 401) window.location.href = '/login';
     }
  };

  // Fetch Users with optional filters and pagination
  const fetchUsers = async (filters = {}, page = 0, rowsPerPage = 10, headers = getAuthHeader()) => {
    if (!headers) return;
    setLoadingUsers(true);
    setFetchError(prev => ({ ...prev, users: null })); // Clear previous user error
    try {
      const cleanFilters = Object.fromEntries(Object.entries(filters).filter(([_, v]) => v != null && v !== '')); // Remove empty filters
      const paginationParams = {
        page: page + 1, // API expects 1-based index
        limit: rowsPerPage,
      };
      const allParams = { ...cleanFilters, ...paginationParams };
      const params = new URLSearchParams(allParams).toString();
      const apiUrl = `${process.env.REACT_APP_API_URL}/api/users?${params}`;
      // console.log(`Fetching users from: ${apiUrl}`); // Removed log
      const response = await axios.get(apiUrl, { headers });
      setUsers(response.data?.records || []); // Expecting { records: [], totalCount: ... }
      setUsersTotalCount(response.data?.totalCount || 0);
    } catch (error) {
      // console.error('Error fetching users:', error); // Removed log
      setFetchError(prev => ({ ...prev, users: 'Failed to load users.' }));
      if (error.response && error.response.status === 401) window.location.href = '/login';
      setUsers([]); // Clear users on error
      setUsersTotalCount(0); // Reset count on error
    } finally {
      setLoadingUsers(false);
    }
  };

  // Fetch Vehicles with optional filters and pagination
  const fetchVehicles = async (filters = {}, page = 0, rowsPerPage = 10, headers = getAuthHeader()) => {
     if (!headers) return;
     setLoadingVehicles(true);
     setFetchError(prev => ({ ...prev, vehicles: null })); // Clear previous vehicle error
     try {
       const cleanFilters = Object.fromEntries(Object.entries(filters).filter(([_, v]) => v != null && v !== ''));
       const paginationParams = {
         page: page + 1, // API expects 1-based index
         limit: rowsPerPage,
       };
       const allParams = { ...cleanFilters, ...paginationParams };
       const params = new URLSearchParams(allParams).toString();
       const apiUrl = `${process.env.REACT_APP_API_URL}/api/vehicles?${params}`;
       // console.log(`Fetching vehicles from: ${apiUrl}`);
       const response = await axios.get(apiUrl, { headers });
       setVehicles(response.data?.records || []); // Expecting { records: [], totalCount: ... }
       setFleetTotalCount(response.data?.totalCount || 0);
       // localStorage.setItem('fleetVehicles', JSON.stringify(response.data?.records || [])); // Consider implications of storing paginated data
     } catch (error) {
       // console.error('Error fetching vehicles:', error);
       setFetchError(prev => ({ ...prev, vehicles: 'Failed to load vehicles.' }));
       if (error.response && error.response.status === 401) window.location.href = '/login';
       setVehicles([]); // Clear vehicles on error
       setFleetTotalCount(0); // Reset count on error
     } finally {
       setLoadingVehicles(false);
     }
   };

   // Fetch Facilities with optional filters, pagination (Memoized)
   const fetchFacilities = useCallback(async (filters = {}, headers = getAuthHeader(), updateState = true, page = 0, rowsPerPage = 10) => {
      if (!headers) return { records: [], totalCount: 0 }; // Return empty structure if no headers
      if (updateState) setLoadingFacilities(true);
      if (updateState) setFetchError(prev => ({ ...prev, facilities: null }));

      try {
        const cleanFilters = Object.fromEntries(Object.entries(filters).filter(([_, v]) => v != null && v !== ''));
        const paginationParams = {
          page: page + 1, // API expects 1-based index
          limit: rowsPerPage,
        };
        const allParams = { ...cleanFilters, ...paginationParams };
        const params = new URLSearchParams(allParams).toString();
        const apiUrl = `${process.env.REACT_APP_API_URL}/api/facilities?${params}`;
        // console.log(`Fetching facilities from: ${apiUrl}`);
        const response = await axios.get(apiUrl, { headers });

        const facilityData = response.data?.records || [];
        const totalCount = response.data?.totalCount || 0;

        if (updateState) {
            setSites(facilityData);
            setSitesTotalCount(totalCount);
        }
        // Return structure consistent with expected response format
        return { records: facilityData, totalCount: totalCount };
      } catch (error) {
        // console.error('Error fetching facilities:', error);
        if (updateState) {
            setFetchError(prev => ({ ...prev, facilities: 'Failed to load sites/facilities.' }));
            setSites([]); // Clear sites on error
            setSitesTotalCount(0); // Reset count on error
        }
        if (error.response && error.response.status === 401) window.location.href = '/login';
        return { records: [], totalCount: 0 }; // Return empty structure on error
      } finally {
        if (updateState) setLoadingFacilities(false);
      }
    }, [getAuthHeader]); // Keep dependency array minimal for useCallback

  // Fetch Drivers with optional filters and pagination
  const fetchDrivers = async (filters = {}, page = 0, rowsPerPage = 10, headers = getAuthHeader()) => {
    if (!headers) return;
    setLoadingDrivers(true);
    setFetchError(prev => ({ ...prev, drivers: null })); // Clear previous driver error
    try {
      // Ensure the role filter is always applied for drivers
      const driverFilters = { ...filters, role: 'driver' };
      const cleanFilters = Object.fromEntries(Object.entries(driverFilters).filter(([_, v]) => v != null && v !== '')); // Remove empty filters
      const paginationParams = {
        page: page + 1, // API expects 1-based index
        limit: rowsPerPage,
      };
      const allParams = { ...cleanFilters, ...paginationParams };
      const params = new URLSearchParams(allParams).toString();
      // Assuming drivers are fetched from the general users endpoint with a role filter
      const apiUrl = `${process.env.REACT_APP_API_URL}/api/users?${params}`;
      // console.log(`Fetching drivers from: ${apiUrl}`); // Keep for debugging if needed
      const response = await axios.get(apiUrl, { headers });
      setDrivers(response.data?.records || []); // Expecting { records: [], totalCount: ... }
      setDriversTotalCount(response.data?.totalCount || 0);
    } catch (error) {
      // console.error('Error fetching drivers:', error); // Keep for debugging if needed
      setFetchError(prev => ({ ...prev, drivers: 'Failed to load drivers.' }));
      if (error.response && error.response.status === 401) window.location.href = '/login';
      setDrivers([]); // Clear drivers on error
      setDriversTotalCount(0); // Reset count on error
    } finally {
      setLoadingDrivers(false);
    }
  };

  // --- Pagination and Search Handlers for Child Components ---

  // Handler for Users pagination change
  const handleUsersPaginationChange = useCallback((newPage, newRowsPerPage) => {
    // console.log("Settings.js: handleUsersPaginationChange triggered with:", newPage, newRowsPerPage); // Keep for debugging if needed
    // Assume filters are managed elsewhere or we refetch with current filters.
    // For now, let's assume we need to know the current filters.
    // This might require lifting filter state up or passing it differently.
    // For simplicity, let's refetch with empty filters for now, but this needs review.
    // TODO: Get current filters to pass to fetchUsers
    const currentFilters = {}; // Placeholder - Needs actual filter state access
    setUsersPage(newPage);
    setUsersRowsPerPage(newRowsPerPage);
    fetchUsers(currentFilters, newPage, newRowsPerPage);
  }, [fetchUsers]); // Add fetchUsers dependency

  // Handler for Users search/filter changes (resets pagination)
  const handleUserSearch = useCallback((filters) => {
    // console.log("Settings.js: handleUserSearch triggered with filters:", filters); // Keep for debugging if needed
    setUsersPage(0); // Reset to first page on new search
    fetchUsers(filters, 0, usersRowsPerPage); // Use current rowsPerPage
  }, [fetchUsers, usersRowsPerPage]); // Add dependencies

  // Handler for Sites pagination change
  const handleSitesPaginationChange = useCallback((newPage, newRowsPerPage) => {
    // console.log("Settings.js: handleSitesPaginationChange triggered with:", newPage, newRowsPerPage);
    // TODO: Get current filters if they need to be preserved during pagination
    const currentFilters = {}; // Placeholder - Assuming filters reset or are handled elsewhere for now
    setSitesPage(newPage);
    setSitesRowsPerPage(newRowsPerPage);
    fetchFacilities(currentFilters, getAuthHeader(), true, newPage, newRowsPerPage);
  }, [fetchFacilities, getAuthHeader]); // Add dependencies

  // Handler for Fleet pagination change
  const handleFleetPaginationChange = useCallback((newPage, newRowsPerPage) => {
    // console.log("Settings.js: handleFleetPaginationChange triggered with:", newPage, newRowsPerPage);
    // Assuming filters are managed by the Fleet component or reset for simple pagination.
    // If filters need to be preserved, they should be accessed from state or passed.
    const currentFilters = {}; // Placeholder: Use actual filters if needed
    setFleetPage(newPage);
    setFleetRowsPerPage(newRowsPerPage);
    fetchVehicles(currentFilters, newPage, newRowsPerPage, getAuthHeader());
  }, [fetchVehicles, getAuthHeader]); // Add dependencies

  // Handler for Drivers pagination change
  const handleDriversPaginationChange = useCallback((newPage, newRowsPerPage) => {
    // console.log("Settings.js: handleDriversPaginationChange triggered with:", newPage, newRowsPerPage); // Keep for debugging if needed
    // Assuming filters are managed by the Drivers component or reset for simple pagination.
    // If filters need to be preserved, they should be accessed from state or passed.
    const currentFilters = {}; // Placeholder: Use actual filters if needed
    setDriversPage(newPage);
    setDriversRowsPerPage(newRowsPerPage);
    fetchDrivers(currentFilters, newPage, newRowsPerPage, getAuthHeader());
  }, [fetchDrivers, getAuthHeader]); // Add dependencies

  // Handler for Sites tab search/filter changes (resets pagination) (Memoized)
  const handleSiteSearch = useCallback(async (filters) => {
     // console.log("Settings.js: handleSiteSearch triggered with filters:", filters);
     setSitesPage(0); // Reset to first page on new search
     // Call fetchFacilities with new filters, reset page, current rowsPerPage, and update state
     await fetchFacilities(filters, getAuthHeader(), true, 0, sitesRowsPerPage);
  }, [fetchFacilities, getAuthHeader, sitesRowsPerPage]); // Add dependencies

  // Handler for Rates tab filter changes (fetches facilities for the Rates' dropdown)
  // Removed incorrect useCallback wrapper
  const handleRateFacilitySearch = async (filters) => {
      // console.log("Settings.js: handleRateFacilitySearch triggered with filters:", filters); // Removed log
      // Fetch facilities based on filters but DON'T update the main 'sites' state
      // Return the result so Rates.js can update its own dropdown state
      const headers = getAuthHeader();
      if (!headers) return [];
      // Maybe add a loading indicator specific to this action?
      try {
          const filteredFacilities = await fetchFacilities(filters, headers, false); // Pass false to prevent updating main state
          return filteredFacilities;
      } catch (error) {
          // console.error("Error fetching filtered facilities for Rates tab:", error); // Removed log
          return []; // Return empty on error
      }
  }; // Removed useCallback wrapper and its dependencies


  // updateSettings might need complete removal or heavy modification
  // if PUT /api/settings is no longer the primary way to save sub-sections.
  // For now, just refresh all data after a general update.
  const updateSettings = async (updatedSettings) => {
    const headers = getAuthHeader();
    if (!headers) return false;
    try {
      await axios.put(`${process.env.REACT_APP_API_URL}/api/settings`, updatedSettings, { headers });
      // Refresh relevant data after update
      fetchUsers({}, headers);
      fetchVehicles({}, headers);
      fetchFacilities({}, headers);
      fetchOtherSettings(headers); // Refresh locations etc.
      return true;
    } catch (error) {
      // console.error('Error updating settings:', error); // Removed log
      if (error.response && error.response.status === 401) window.location.href = '/login';
      return false;
    }
  };

  // Tab change handler
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Display potential fetch errors
  const renderError = () => {
     if (!fetchError) return null;
     const errorMessages = Object.values(fetchError).filter(Boolean);
     if (errorMessages.length === 0) return null;
     return (
        <Typography color="error" sx={{ mb: 2 }}>
           {errorMessages.join(' ')}
        </Typography>
     );
  }

  return (
    <>
      <SideMenu />
      <Container maxWidth={false}>
        <Box mt={4} mb={2} display="flex" justifyContent="space-between" alignItems="center" >
          <Typography variant="h4">Settings</Typography>
        </Box>
        {renderError()} {/* Display errors */}
        <Tabs value={Math.min(tabValue, Math.max(visibleTabs.length - 1, 0))} onChange={handleTabChange} aria-label="settings tabs" variant="scrollable" scrollButtons="auto" data-tour="settings-tabs">
          {visibleTabs.map((t) => <Tab key={t.key} label={t.label} data-tour={`settings-tab-${t.key}`} />)}
        </Tabs>

        {/* Render the selected tab */}
        <Box data-tour="settings-panel">
        {activeTab === 'rates' && <RateManagement readOnly={sitesRatesReadOnly} />}
        {activeTab === 'dashboard' && <DashboardSettings />}
        {activeTab === 'sites' && (
            <Sites
                readOnly={sitesRatesReadOnly}
                sites={sites}
                // setSites prop removed as data fetching is handled here
                onSearch={handleSiteSearch} // Pass search handler (resets pagination)
                page={sitesPage}
                rowsPerPage={sitesRowsPerPage}
                totalCount={sitesTotalCount}
                onPaginationChange={handleSitesPaginationChange} // Pass pagination handler
                loading={loadingFacilities} // Pass loading state
                error={fetchError?.facilities} // Pass error state
            />
        )}
        {activeTab === 'fleet' && (
          <Fleet
            readOnly={orgAdminReadOnly}
            vehicles={vehicles}
            setVehicles={setVehicles} // Pass setVehicles prop to fix TypeError
            onSearch={(searchFilters) => fetchVehicles(searchFilters, 0, fleetRowsPerPage, getAuthHeader())} // Reset to page 0 on search
            page={fleetPage}
            rowsPerPage={fleetRowsPerPage}
            totalCount={fleetTotalCount}
            onPaginationChange={handleFleetPaginationChange}
            loading={loadingVehicles} // Pass loading state for consistency
            error={fetchError?.vehicles} // Pass error state for consistency
          />
        )}
        {activeTab === 'users' && (
          <Users
            readOnly={orgAdminReadOnly}
            users={users}
            // setUsers prop removed as data fetching is handled here
            onSearch={handleUserSearch} // Use the new search handler
            page={usersPage}
            rowsPerPage={usersRowsPerPage}
            totalCount={usersTotalCount}
            onPaginationChange={handleUsersPaginationChange} // Pass the pagination handler
            loading={loadingUsers} // Pass loading state
            error={fetchError?.users} // Pass error state
          />
        )}
        {activeTab === 'drivers' && (
          <Drivers
            readOnly={orgAdminReadOnly}
            drivers={drivers}
            page={driversPage}
            rowsPerPage={driversRowsPerPage}
            totalCount={driversTotalCount}
            onPaginationChange={handleDriversPaginationChange}
            onSearch={(searchFilters) => {
              setDriversPage(0); // Reset page on new search
              fetchDrivers(searchFilters, 0, driversRowsPerPage, getAuthHeader());
            }}
            loading={loadingDrivers}
            error={fetchError?.drivers}
          />
        )}
        {activeTab === 'backgroundChecks' && <BackgroundChecksSettings />}
        {activeTab === 'insurance' && <InsuranceSettings />}
        {activeTab === 'incentives' && <IncentiveProgram />}
        {activeTab === 'hiringMarketing' && <HiringMarketingLink />}
        {activeTab === 'terms' && <TermsSettings readOnly={orgAdminReadOnly} />}
        </Box>
      </Container>
    </>
  );
}

export default Settings;
